/**
 * Parse backend API errors into user-friendly messages
 * Handles multiple error formats including Pydantic validation errors
 * 
 * @param {Object|string} error - The error object from API
 * @param {string} defaultMessage - Default message if error cannot be parsed
 * @returns {string} User-friendly error message
 */
export function parseApiError(error, defaultMessage = "An error occurred") {
  // If error is already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // Check if error has a detail property
  if (error?.detail) {
    // Handle Pydantic validation error format (array of objects)
    if (Array.isArray(error.detail)) {
      return error.detail
        .map(err => {
          // Extract field name from location array
          const field = err.loc && err.loc.length > 0 
            ? err.loc.join('.').replace('body.', '') 
            : 'field';
          
          // Return formatted error message
          return `${field}: ${err.msg || 'Invalid value'}`;
        })
        .join(', ');
    }
    
    // Handle simple string detail
    if (typeof error.detail === 'string') {
      return error.detail;
    }
    
    // Handle object detail (fallback to JSON string)
    if (typeof error.detail === 'object') {
      return JSON.stringify(error.detail);
    }
  }

  // Check for error message property
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }

  // Check for error property
  if (error?.error && typeof error.error === 'string') {
    return error.error;
  }

  // Fallback to default message
  return defaultMessage;
}

/**
 * Format validation errors for display
 * Converts Pydantic validation errors to readable format
 * 
 * @param {Array} validationErrors - Array of validation error objects
 * @returns {string} Formatted error message
 */
export function formatValidationErrors(validationErrors) {
  if (!Array.isArray(validationErrors)) {
    return "Validation error occurred";
  }

  return validationErrors
    .map(err => {
      const field = err.loc && err.loc.length > 0
        ? err.loc[err.loc.length - 1] // Get last element (field name)
        : 'field';
      
      const message = err.msg || 'Invalid value';
      
      // Make field name more user-friendly
      const friendlyField = field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase()
        .replace(/^\w/, c => c.toUpperCase());
      
      return `${friendlyField}: ${message}`;
    })
    .join('\n');
}
